import './LdapSettings.css';
import 'containers/Stepper/Stepper.css';

import { connectAsAny } from 'utils/redux/connectAsAny';

import { ca, configurations, dataModified, groupErrors } from './ldapPropTypes';

import { isNonNullable } from '@ipfabric/utils';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import { toggleConfirmDialog } from 'containers/ConfirmDialog/actions';
import { uploadCA } from 'containers/FileUploadOverlay/actions';
import LTEBox from 'containers/LTEBox';
import { StepperItem } from 'containers/Stepper';
import { Map as ImmutableMap, List } from 'immutable';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { pipe } from 'ramda';
import { PureComponent } from 'react';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import fetch from 'utils/fetch';
import handleEvent from 'utils/handleEvent';
import ConfigForm from './ConfigForm';
import RoleAssignmentsForm from './RoleAssignmentsForm';
import {
  clearGroupError,
  clearGroupErrors,
  clearPage,
  confirmDeleteAllRoleAssignments,
  deleteConfiguration,
  domainOptionsChange,
  fetchCA,
  fetchData,
  openFileUpload,
  saveForm,
} from './actions';
import {
  AD_DEFAULTS,
  DELETE_ROLE_ASSIGNMENT_DIALOG_NAME,
  NEW_ROLE_ITEM,
  PAGE_URL,
} from './constants';
import { configFormName, roleAssignmentFormName } from './formName';
import {
  makeSelectCA,
  makeSelectConfiguration,
  makeSelectDataModified,
  makeSelectGroupErrors,
} from './selectors';

const NEW_ITEM = new ImmutableMap({
  id: 'new',
  type: 'ad',
  serverType: 'list',
  server: new List([new ImmutableMap()]),
  useLdaps: false,
  serviceName: 'LDAP',
  searchLogin: AD_DEFAULTS.searchLogin,
  roleAssignments: new List([NEW_ROLE_ITEM]),
  nestedGroups: false,
});

@fetch({ actions: [fetchData], fetchOnLocationChange: false })
class LdapSettings extends PureComponent {
  static propTypes = {
    ca: ca,
    configuration: configurations,
    dataModified: dataModified,
    groupErrors: groupErrors,
    navigate: PropTypes.func.isRequired,
    msg: PropTypes.object.isRequired,
    onClearGroupError: PropTypes.func.isRequired,
    onClearGroupErrors: PropTypes.func.isRequired,
    onClearPage: PropTypes.func.isRequired,
    onConfirmDeleteAllRoleAssignments: PropTypes.func.isRequired,
    onDeleteConfiguration: PropTypes.func.isRequired,
    onDomainOptionsChange: PropTypes.func.isRequired,
    onFetchCA: PropTypes.func.isRequired,
    onOpenFileUpload: PropTypes.func.isRequired,
    onSaveForm: PropTypes.func.isRequired,
    onToggleConfirmDialog: PropTypes.func.isRequired,
    params: PropTypes.object.isRequired,
  };

  componentWillUnmount() {
    this.props.onClearPage();
  }

  getInitialValues = () => {
    const {
      configuration,
      params: { configurationId, step },
    } = this.props;
    const newConfiguration = configurationId === 'new';
    if (newConfiguration) {
      return NEW_ITEM;
    }
    const foundConfiguration = configuration.find(
      (config) => config.get('id') === configurationId,
    );
    if (foundConfiguration) {
      if (step === 'config') {
        if (
          !foundConfiguration.has('server') ||
          !foundConfiguration.get('server').size
        ) {
          return foundConfiguration.set(
            'server',
            new List([new ImmutableMap()]),
          );
        }
        return foundConfiguration;
      }
      if (step === 'role-assignments') {
        if (!foundConfiguration.has('roleAssignments')) {
          return new ImmutableMap({
            roleAssignments: new List([new ImmutableMap()]),
          });
        }
        return new ImmutableMap({
          roleAssignments: foundConfiguration.get('roleAssignments'),
        });
      }
    }
    return new ImmutableMap();
  };

  getFieldTitle = (id, foundField) => {
    const {
      msg: { administrationLdapSettings: msg },
    } = this.props;
    if (id === 'new') {
      return this.getAddConfigurationTitle(msg);
    }
    return (
      isNonNullable(foundField) &&
      List.isList(foundField.get('accountSuffixes')) &&
      foundField.get('accountSuffixes').join(', ')
    );
  };

  getAddConfigurationTitle = (msg) => (
    <span>
      <i className='fa fa-plus mr-lgu-2 inline-block align-middle' />
      <span className='inline-block align-middle'>
        {get(msg, 'btnAddConfiguration')}
      </span>
    </span>
  );

  getStep = () => {
    const {
      params: { step },
    } = this.props;
    if (step === 'config') {
      return 1;
    }
    if (step === 'role-assignments') {
      return 2;
    }
    return 0;
  };

  getDeleteRoleAssignmentDialogMessage = () => {
    const {
      msg: { administrationLdapSettings: msg },
    } = this.props;
    return {
      header: msg.deleteAllRoleAssignmentsDialog.header,
      btnConfirm: msg.deleteAllRoleAssignmentsDialog.btnConfirm,
      btnSecondaryConfirm:
        msg.deleteAllRoleAssignmentsDialog.btnSecondaryConfirm,
    };
  };

  handleEditConfiguration = (id) =>
    handleEvent(() => {
      const {
        navigate,
        params: { configurationId },
        onDomainOptionsChange,
      } = this.props;
      if (configurationId !== id) {
        navigate(`${PAGE_URL}/${id}/config`);
      } else {
        onDomainOptionsChange({
          dataModified: false,
        });
        navigate(PAGE_URL);
      }
    });

  handleSubmitConfig = handleEvent((values) => {
    const {
      onDomainOptionsChange,
      onSaveForm,
      params: { configurationId, step },
    } = this.props;

    onDomainOptionsChange({
      dataModified: false,
    });

    const cleanValues = pipe(
      (v) =>
        v.update('server', (server) =>
          List.isList(server) ? server.filter((item) => item.size) : server,
        ),
      (v) => (v.get('type') !== 'ad' ? v.set('nestedGroups', false) : v),
    )(values);

    onSaveForm({
      values: cleanValues,
      id: configurationId,
      step,
      returnUrl: this.returnUrl,
    });
    this.returnUrl = null;
  });

  handleSubmitRoleAssignments = handleEvent((values) => {
    const {
      onSaveForm,
      params: { configurationId, step },
    } = this.props;

    const cleanValues = pipe(
      (v) =>
        v.update('roleAssignments', (roleAssignments) =>
          roleAssignments.filter((item) => item.size),
        ),
      (v) => v.set('id', configurationId),
    )(values);

    onSaveForm({
      values: cleanValues,
      id: configurationId,
      step,
      returnUrl: this.returnUrl,
    });
    this.returnUrl = null;
  });

  handleSetReturnUrl = handleEvent((returnUrl) => {
    this.returnUrl = returnUrl;
  });

  handleDeleteRoleAssignmentsConfirm = handleEvent(() => {
    this.props.onConfirmDeleteAllRoleAssignments();
  });

  render() {
    const {
      ca,
      dataModified,
      configuration,
      groupErrors,
      msg: {
        administrationLdapSettings: msg,
        leaveDialogue: leaveDialogueMessages,
      },
      onClearGroupError,
      onClearGroupErrors,
      onDeleteConfiguration,
      onDomainOptionsChange,
      onFetchCA,
      onOpenFileUpload,
      onToggleConfirmDialog,
      params: { configurationId, step },
      navigate,
    } = this.props;
    const newConfiguration = configurationId === 'new';
    return (
      <div className='ldap-settings m-4'>
        <Helmet title={get(msg, 'pageTitle')} />
        <ConfirmDialog
          id={DELETE_ROLE_ASSIGNMENT_DIALOG_NAME}
          onClickConfirm={this.handleDeleteRoleAssignmentsConfirm}
          msg={this.getDeleteRoleAssignmentDialogMessage()}
          bodyComponent={get(msg, 'deleteAllRoleAssignmentsDialog.body')}
          btnConfirmVariant='primary'
          btnConfirmAccent='positive'
          btnCancelVariant='secondary'
          btnCancelAccent='positive'
        />
        <LTEBox
          bodyClassName='box-body box-body-configuration'
          bodyComponent={
            <div>
              {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code */}
              {configuration.push(NEW_ITEM).map((config) => {
                const id = config.get('id');
                return (
                  <div
                    key={config.get('id')}
                    className='box box-solid box-configuration'
                  >
                    <div
                      className='box-header box-header-configuration with-border'
                      onClick={this.handleEditConfiguration(config.get('id'))}
                    >
                      <h3 className='ldap-settings__accordion-title box-title'>
                        {this.getFieldTitle(id, config)}
                      </h3>
                      <i
                        className={`fa fa-angle-${
                          configurationId === id ? 'up' : 'down'
                        } fa-2x ldap-settings__accordion-arrow`}
                      />
                    </div>
                    {configurationId === id ? (
                      <div>
                        <div className='box-body'>
                          <div className='stepper m-3'>
                            <StepperItem
                              completed={step === 'role-assignments'}
                              count={1}
                              currentStep={this.getStep()}
                              first
                              linkTo={
                                !newConfiguration
                                  ? `${PAGE_URL}/${configurationId}/config`
                                  : ''
                              }
                            >
                              {get(msg, 'configurations.editConfig')}
                            </StepperItem>
                            <StepperItem
                              completed={false}
                              count={2}
                              currentStep={this.getStep()}
                              last
                              linkTo={
                                !newConfiguration
                                  ? `${PAGE_URL}/${configurationId}/role-assignments`
                                  : ''
                              }
                              wrapperClassName={
                                newConfiguration
                                  ? 'ldap-settings-stepper-role-assignments--new-configuration'
                                  : ''
                              }
                            >
                              {get(msg, 'configurations.groupPermissions')}
                            </StepperItem>
                          </div>
                          <div>
                            {step === 'config' ? (
                              <ConfigForm
                                ca={ca}
                                configuration={configuration}
                                configurationId={configurationId}
                                dataModified={dataModified}
                                form={`${configFormName}_${configurationId}`}
                                navigate={navigate}
                                initialValues={this.getInitialValues()}
                                leaveDialogueMessages={leaveDialogueMessages}
                                msg={msg}
                                newConfiguration={newConfiguration}
                                onDeleteConfiguration={onDeleteConfiguration}
                                onDomainOptionsChange={onDomainOptionsChange}
                                onFetchCA={onFetchCA}
                                onOpenFileUpload={onOpenFileUpload}
                                onSetReturnUrl={this.handleSetReturnUrl}
                                onSubmit={this.handleSubmitConfig}
                                onToggleConfirmDialog={onToggleConfirmDialog}
                              />
                            ) : null}
                            {step === 'role-assignments' ? (
                              <RoleAssignmentsForm
                                configuration={configuration}
                                configurationId={configurationId}
                                dataModified={dataModified}
                                form={`${roleAssignmentFormName}_${configurationId}`}
                                groupErrors={groupErrors}
                                navigate={navigate}
                                initialValues={this.getInitialValues()}
                                leaveDialogueMessages={leaveDialogueMessages}
                                msg={msg}
                                newConfiguration={newConfiguration}
                                onClearGroupError={onClearGroupError}
                                onClearGroupErrors={onClearGroupErrors}
                                onDomainOptionsChange={onDomainOptionsChange}
                                onSetReturnUrl={this.handleSetReturnUrl}
                                onSubmit={this.handleSubmitRoleAssignments}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          }
          className='box box-primary box-configurations'
          title={get(msg, ['ldapConfigurationTitle'])}
        />
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  ca: makeSelectCA(),
  configuration: makeSelectConfiguration(),
  dataModified: makeSelectDataModified(),
  groupErrors: makeSelectGroupErrors(),
});

function mapDispatchToProps(dispatch) {
  return {
    onClearGroupError: (args) => dispatch(clearGroupError(args)),
    onClearGroupErrors: () => dispatch(clearGroupErrors()),
    onClearPage: () => dispatch(clearPage()),
    onConfirmDeleteAllRoleAssignments: () =>
      dispatch(confirmDeleteAllRoleAssignments()),
    onDeleteConfiguration: (args) => dispatch(deleteConfiguration(args)),
    onDomainOptionsChange: (args) => dispatch(domainOptionsChange(args)),
    onFetchCA: () => dispatch(fetchCA()),
    onOpenFileUpload: () => dispatch(openFileUpload()),
    onSaveForm: (args) => dispatch(saveForm(args)),
    onToggleConfirmDialog: (args) => dispatch(toggleConfirmDialog(args)),
    onUploadCA: (args) => dispatch(uploadCA(args)),
  };
}

export default connectAsAny(mapStateToProps, mapDispatchToProps)(LdapSettings);
